import { ViewType } from '@audacy-clients/client-services';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import DataServices, {
  FeaturedPlayable,
  View,
} from '@audacy-clients/client-services/src/dataServices/DataServices';
import { CacheTimes } from '@audacy-clients/core/atoms/helpers/constants';
import { getMarketIdsString } from '@audacy-clients/core/atoms/helpers/markets';
import { refreshableSelectorFamily } from '@audacy-clients/core/atoms/helpers/refreshable';
import { locationMarketsSelector } from '@audacy-clients/core/atoms/location';
import { wrapContentObject } from '@audacy-clients/core/atoms/wrappers/content';
import { type IView, wrapView } from '@audacy-clients/core/atoms/wrappers/modules';
import clientServices from '@audacy-clients/core/utils/clientServices';
import { type SerializableParam, useRecoilValue } from 'recoil';

import { RequestType, type TModuleRequest } from './requests';

const getView = async (
  request: TModuleRequest,
  dataServices: DataServices,
  markets?: string,
): Promise<View | undefined> => {
  try {
    if (request.type === RequestType.LandingPage || request.type === RequestType.ContentPage) {
      switch (request.type) {
        case RequestType.ContentPage:
          return await dataServices.getView(request.viewId, request.contentId, markets);

        case RequestType.LandingPage:
          return await dataServices.getView(request.viewId, undefined, markets);
      }
    }
    return await dataServices.getPage(request.path, markets);
  } catch (error) {
    if (error instanceof Error) {
      console.error(error);
      // If a 404 is found we want to throw here, so that the error layer can properly display an error message
      if(error.message.includes("status: 404")) {
        throw error;
      }
    } else {
      console.error('Unknown error type:', error);
    }
  }
};

const getFeaturedPlayable = async (
  contentId: string | undefined,
  dataServices: DataServices,
): Promise<FeaturedPlayable | undefined> => {
  if (!contentId) {
    return undefined;
  }

  return await dataServices.getFeaturedPlayable(contentId);
};

const performRequest = async (
  request: TModuleRequest,
  dataServices: DataServices,
  markets?: string,
): Promise<IView | undefined> => {
  if (request.type === RequestType.LandingPage || request.type === RequestType.ContentPage) {
    const [view, featuredPlayable] = await Promise.all([
      getView(request, dataServices, markets),
      request.type === RequestType.ContentPage
        ? getFeaturedPlayable(request.contentId, dataServices)
        : undefined,
    ]);

    return view ? wrapView(view, featuredPlayable) : undefined;
  } else {
    const view = await getView(request, dataServices, markets);
    const rootObj = wrapContentObject(view?.contentObj);
    const featuredPlayable = rootObj?.id
      ? await getFeaturedPlayable(rootObj.id, dataServices)
      : undefined;
    return view ? wrapView(view, featuredPlayable) : undefined;
  }
};

type TQueryParams = TModuleRequest & SerializableParam;

export const { selector: modulesSelector, useRefreshCache: useRefreshModules } =
  refreshableSelectorFamily<IView, TQueryParams>({
    get:
      (moduleRequest) =>
      async ({ get }) => {
        const { markets, isLoading } = get(locationMarketsSelector);
        const marketsString = getMarketIdsString(markets);

        const defaultView = {
          type: ViewType.MODULES,
          getContent: () => undefined,
          id: 'empty-module-placeholder',
          modules: [],
          uniqueId: Date.now().toString(),
        };

        if (isLoading) {
          return defaultView;
        }

        return (await performRequest(moduleRequest, clientServices.getDataServices(), marketsString)) || defaultView;
      },
    key: 'Modules',
    cacheLimit: CacheTimes.limit10m,
  });

export const useModules = (request: TModuleRequest) => useRecoilValue(modulesSelector(request));

export { getView };
